import React, { useEffect, useMemo, useState } from 'react';
import { CaretDown, CaretRight, Paperclip, Tray } from '@phosphor-icons/react';
import {
  fetchTasks, fetchAssignees, updateTask, PRIORITY_STYLE,
} from '../api/whatsappTasksApi';
import AttachmentList from '../components/AttachmentList';
import { formatUtcToIst } from '../../tickets/utils/datetime';
import { TicketDetailLink } from '../../tickets/ticketNav';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';

const selectStyle = { display: 'block', marginTop: '4px', padding: '8px 10px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '12px' };

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ fontSize: '11px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
      <div style={{ fontSize: '12px', color: '#111827' }}>{value}</div>
    </div>
  );
}

function TaskDetailAndTriage({ task, staff, onDone }) {
  const [priority, setPriority] = useState(task.priority || 'medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueBy, setDueBy] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleAssign = async () => {
    if (!assignedTo) { setError('Choose a team member to assign to.'); return; }
    setSaving(true);
    setError(null);
    try {
      await updateTask(task.task_no, {
        priority,
        assigned_to: assignedTo,
        due_by: dueBy ? new Date(dueBy).toISOString() : undefined,
      });
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to assign ticket');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '10px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '4px', marginBottom: '6px' }}>
        <Field label="Ticket ID" value={task.task_no} />
        <Field label="Reporter" value={task.sender_name} />
        <Field label="Phone" value={task.sender_phone} />
        <Field label="Group" value={task.group_name} />
        <Field label="Product / Team" value={task.product_name || 'Unmapped — link group in Product Management'} />
        <Field label="Received" value={formatUtcToIst(task.received_at)} />
        <Field label="Category" value={task.category} />
      </div>

      {task.fasttext_label === 'strip' && task.original_message !== task.task_text && (
        <blockquote style={{ margin: '0 0 8px', paddingLeft: '12px', borderLeft: '3px solid #E5E7EB', color: '#9CA3AF', fontSize: '12px' }}>
          Original message: "{task.original_message}"
        </blockquote>
      )}
      <blockquote style={{ margin: '0 0 16px', paddingLeft: '12px', borderLeft: '3px solid #D1D5DB', color: '#374151', fontSize: '12px' }}>
        "{task.task_text}"
      </blockquote>

      <AttachmentList attachments={task.attachments} />

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ fontSize: '12px', color: '#374151' }}>
          Priority
          <select value={priority} onChange={(e) => setPriority(e.target.value)} style={selectStyle}>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>
        <label style={{ fontSize: '12px', color: '#374151' }}>
          Assign to
          <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} style={selectStyle}>
            <option value="">Select member…</option>
            {staff.map((s) => (
              <option key={s.user_id} value={s.user_id}>{s.name}{s.role ? ` (${s.role})` : ''}</option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: '12px', color: '#374151' }}>
          Due by
          <input type="datetime-local" value={dueBy} onChange={(e) => setDueBy(e.target.value)} style={selectStyle} />
        </label>
        <button
          type="button"
          onClick={handleAssign}
          disabled={saving}
          style={{ padding: '5px 10px', background: '#002FA7', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
        >
          {saving ? 'Assigning…' : 'Assign & notify'}
        </button>
        <TicketDetailLink
          ticketNo={task.ticket_no || task.task_no}
          style={{ fontSize: '12px', color: '#002FA7', textDecoration: 'none' }}
        >
          Open ITSM ticket →
        </TicketDetailLink>
      </div>
      {error && <p style={{ color: '#B91C1C', fontSize: '12px', marginTop: '8px' }}>{error}</p>}
    </div>
  );
}

function TaskRow({ task, isSelected, onClick }) {
  const pr = PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.medium;
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px 8px 32px',
        borderTop: '1px solid #F3F4F6', cursor: 'pointer',
        background: isSelected ? '#EFF6FF' : 'white',
        borderLeft: isSelected ? '3px solid #002FA7' : '3px solid transparent',
      }}
    >
      <span style={{ padding: '3px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', background: pr.bg, color: pr.color, flexShrink: 0 }}>
        {pr.label.toUpperCase()}
      </span>
      <span style={{ flex: 1, fontSize: '12px', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</span>
      {task.attachments && task.attachments.length > 0 && <Paperclip size={14} color="#9CA3AF" />}
    </div>
  );
}

function InboxPage({ refreshKey }) {
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedTaskNo, setSelectedTaskNo] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchTasks({ status: 'new' }),
      fetchAssignees().catch(() => []),
    ])
      .then(([t, s]) => { setTasks(t); setStaff(s || []); })
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load inbox'))
      .finally(() => setLoading(false));
  }, [refreshKey, reloadKey]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const key = t.group_name || 'Unknown group';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return Array.from(map.entries());
  }, [tasks]);

  const selectedTask = tasks.find((t) => t.ticket_no === selectedTaskNo) || tasks.find((t) => t.task_no === selectedTaskNo) || null;

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  if (loading) return <SkeletonBlock rows={5} />;
  if (error) return <p style={{ color: '#B91C1C' }}>{error}</p>;

  return (
    <div>
      <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '6px' }}>Unassigned Inbox ({tasks.length})</h3>

      {groups.length === 0 && (
        <EmptyState
          icon={Tray}
          title="Inbox is empty"
          description="No new tickets waiting on triage."
        />
      )}

      {groups.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '4fr 8fr', gap: '10px', alignItems: 'start' }}>
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
            {groups.map(([groupName, groupTasks]) => {
              const isGroupExpanded = expandedGroups.has(groupName);
              return (
                <div key={groupName}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupName)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '6px 10px', background: '#F9FAFB', border: 'none', borderTop: '1px solid #E5E7EB',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    {isGroupExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{groupName}</span>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>({groupTasks.length})</span>
                  </button>

                  {isGroupExpanded && groupTasks.map((t) => (
                    <TaskRow
                      key={t.task_no}
                      task={t}
                      isSelected={selectedTaskNo === t.task_no}
                      onClick={() => setSelectedTaskNo(t.task_no)}
                    />
                  ))}
                </div>
              );
            })}
          </div>

          <div>
            {selectedTask ? (
              <TaskDetailAndTriage
                task={selectedTask}
                staff={staff}
                onDone={() => { setSelectedTaskNo(null); setReloadKey((k) => k + 1); }}
              />
            ) : (
              <div style={{ padding: '32px', textAlign: 'center', color: '#9CA3AF', border: '1px dashed #E5E7EB', borderRadius: '8px', fontSize: '12px' }}>
                Select a ticket from the left to view its details.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default InboxPage;
